// A job in the client card's work tree, folded down to its publications.
//
// ONE component for both kinds of job, which is the point. A topic inside a retainer
// and a standalone one-off differ in where they SIT — under a retainer, or in the
// section below it — and in nothing about what they contain. Both hold work, both
// accumulate coverage, and both open the same way to show it. Giving the one-off its
// own component would have meant two places to fix the day a publication row changes.
//
// So a brokerage job's coverage is reachable in exactly one click from the work tab,
// where a topic's takes two. That asymmetry is the truth about the records: the
// retainer level genuinely exists for one and genuinely does not for the other, and
// wrapping the one-offs in a fake "no retainer" group to even up the depth would be
// worse than the asymmetry it hid.
//
// PUBLICATIONS ARE NOT A THIRD FOLD. They render as a table the moment the job opens,
// and each publication's files render inline in its own row exactly as they do on the
// job screen. Two folds is the whole depth of this tree.
import { useNavigate } from 'react-router-dom';
import { useLookups } from '../../../context/LookupsContext';
import he from '../../../i18n/he';
import Collapsible from '../../../components/Collapsible';
import StatusBadge from '../../../components/StatusBadge';
import Button from '../../../components/Button';
import PublicationList from '../../../components/PublicationList';
import WorkFilesBlock from '../../../components/WorkFilesBlock';
import { JobTermText, BeyondRetainerBadge } from '../../../components/JobTerm';

export default function JobBlock({
  job, publications, workFiles, onWorkFilesChange, open, onToggle, onEditPublication, onDeletePublication, showStream,
}) {
  const navigate = useNavigate();
  const lookups = useLookups();
  const count = publications.length;

  return (
    <Collapsible
      level={2}
      open={open}
      onToggle={onToggle}
      title={job.title}
      badges={
        <>
          {/* A topic that bills its own brokerage margin, marked the same way the
              retainer screen marks it — it is still this retainer's topic, but its
              invoices are its own. */}
          {job.income_source === 'own' && job.retainer_id && (
            <span className="badge blue">{he.retainers.ownIncome}</span>
          )}
          {/* Only a standalone job has a revenue stream and an issuer of its own —
              the server nulls both on a topic, so the badge would be empty there. */}
          {showStream && job.revenue_stream && (
            <span className="badge gray">{lookups.streamLabel(job.revenue_stream)}</span>
          )}
          <StatusBadge kind="job" value={job.status} />
          {/* The dates ride in the COLLAPSED header, with the rest of what this row is,
              because the whole point of them is to be legible without opening anything.
              Rendered only when the job has some — a badge reading "no dates" on every
              job that never needed them is the noise this header exists to avoid. */}
          {job.term && (job.term.startDate || job.term.endDate) && (
            <span className="badge gray"><JobTermText term={job.term} /></span>
          )}
          <BeyondRetainerBadge term={job.term} />
          {/* The count is what makes a retainer with a dozen topics a scan rather
              than a hunt: you can see which jobs carry coverage without opening any
              of them. Shown open or closed — it is context, not just a fold hint. */}
          <span className="badge gray">{count} {he.clientCard.publicationsCount}</span>
        </>
      }
      meta={
        <Button small variant="subtle" onClick={() => navigate(`/jobs/${job.id}`)}>
          {he.clientCard.openJob}
        </Button>
      }
    >
      <h3>{he.publications.title}</h3>
      {/* Editable here, as it was on the flat tab this replaced: spotting something
          wrong and having to go hunting for the job it came from is exactly the walk
          the client card exists to avoid. Adding a publication still belongs to the
          job screen — a new one needs a job to come out of — which is what the
          "open the job" button above is for.

          `showJob` is off: the job is the fold you are inside. */}
      <PublicationList
        publications={publications}
        onEdit={onEditPublication}
        onDelete={onDeletePublication}
        empty={{ text: he.empty.jobPublications }}
      />
      {/* What a content writer sent in (064). Only rendered when there is something —
          an empty block on every job in the tree would be noise, and the coverage list
          above already says what the job has. */}
      {workFiles && workFiles.length > 0 && (
        <WorkFilesBlock
          files={workFiles}
          onChange={onWorkFilesChange}
          heading={he.workFiles.title}
        />
      )}
    </Collapsible>
  );
}
